/**
 * Date math for the calendar week + month grid views (Phase C.1.6).
 *
 * Convention: every Date returned is "noon in Pacific" for the represented
 * day — gives us safe arithmetic that won't slip a day across DST boundaries.
 * Callers that need ISO at the storage layer should convert explicitly.
 */

export const PT_TZ = "America/Los_Angeles";
const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

/** Stable YYYY-MM-DD key in Pacific time. Useful for grouping events by day. */
export function dateKey(d: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}

/** Construct a Date for "noon in PT" on the given calendar day. */
export function ptNoonOf(year: number, monthIndex: number, day: number): Date {
  // 12:00 PT ≈ 19:00 or 20:00 UTC depending on DST. Build via toLocale round-trip.
  // For arithmetic purposes we just need a stable reference inside the day.
  return new Date(Date.UTC(year, monthIndex, day, 20, 0, 0));
}

/** Parse "YYYY-MM-DD" → Date at PT noon. */
export function fromDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return ptNoonOf(y, m - 1, d);
}

/** Today at PT noon. */
export function todayPT(): Date {
  const key = dateKey(new Date());
  return fromDateKey(key);
}

/** Sunday of the week containing `d`, at PT noon. */
export function weekStartSun(d: Date): Date {
  const key = dateKey(d);
  const local = fromDateKey(key);
  // Use the localized weekday name to find Sunday offset reliably.
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: PT_TZ,
    weekday: "short",
  }).format(local);
  const offset = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[
    weekday as "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat"
  ];
  return new Date(local.getTime() - offset * DAY_MS);
}

/** 7 days starting Sunday. */
export function weekDaysSun(anchor: Date): Date[] {
  const start = weekStartSun(anchor);
  const out: Date[] = [];
  for (let i = 0; i < 7; i++) out.push(new Date(start.getTime() + i * DAY_MS));
  return out;
}

/** First day rendered in the month grid (the Sunday on/before the month's 1st). */
export function monthGridStart(anchor: Date): Date {
  const key = dateKey(anchor);
  const [y, m] = key.split("-").map(Number);
  const firstOfMonth = ptNoonOf(y, m - 1, 1);
  return weekStartSun(firstOfMonth);
}

/** 42-cell grid (6 rows × 7 cols) covering the anchor's month. */
export function monthGridDays(anchor: Date): Date[] {
  const start = monthGridStart(anchor);
  const out: Date[] = [];
  for (let i = 0; i < 42; i++) out.push(new Date(start.getTime() + i * DAY_MS));
  return out;
}

/** ±7 days, snapped back to Sunday. */
export function addWeeks(anchor: Date, n: number): Date {
  return weekStartSun(new Date(anchor.getTime() + n * 7 * DAY_MS));
}

/** ±1 month, snapped to month-start (PT noon). */
export function addMonths(anchor: Date, n: number): Date {
  const key = dateKey(anchor);
  const [y, m] = key.split("-").map(Number);
  return ptNoonOf(y, m - 1 + n, 1);
}

/** True when both dates fall on the same Pacific calendar day. */
export function isSameDay(a: Date, b: Date): boolean {
  return dateKey(a) === dateKey(b);
}

/** True when `d` is today in Pacific. */
export function isToday(d: Date): boolean {
  return dateKey(d) === dateKey(new Date());
}

/** True when `d`'s month matches `anchor`'s month. */
export function isSameMonth(d: Date, anchor: Date): boolean {
  return dateKey(d).slice(0, 7) === dateKey(anchor).slice(0, 7);
}

/** Pacific "6 AM" / "12 PM" / etc for hour-row labels in week view. */
export function formatHour(h: number): string {
  const dt = new Date(2026, 0, 1, h, 0, 0); // local — only the hour matters
  return dt
    .toLocaleTimeString("en-US", { hour: "numeric", hour12: true })
    .toLowerCase();
}

/** Hours-since-Pacific-midnight as a float, for week-view event positioning. */
export function ptHoursSinceMidnight(iso: string): number {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: PT_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(d)
    .split(":");
  return Number(parts[0]) + Number(parts[1]) / 60;
}

/** PT-midnight ms for the given Date — used to compute hours offset across midnight. */
export function ptMidnightMs(d: Date): number {
  const key = dateKey(d);
  const [y, m, day] = key.split("-").map(Number);
  // Reverse-iterate to find the UTC moment whose PT rendering is 00:00 on the
  // given day. Three passes converge under DST.
  let guess = new Date(Date.UTC(y, m - 1, day, 8, 0, 0));
  for (let i = 0; i < 3; i++) {
    const ptHr = ptHoursSinceMidnight(guess.toISOString());
    if (ptHr === 0) break;
    guess = new Date(guess.getTime() - ptHr * HOUR_MS);
  }
  return guess.getTime();
}

/**
 * Window of a one-time event onto a specific calendar day for the week view's
 * per-day-chips behavior. If the event spans midnight, this returns the chunk
 * that falls inside `day` — start/end clamped to the day's PT-midnight bounds.
 * Returns null if the event doesn't touch this day at all.
 */
export function eventChunkForDay(
  startISO: string,
  endISO: string,
  day: Date,
): { startHour: number; endHour: number } | null {
  const dayStartMs = ptMidnightMs(day);
  const dayEndMs = dayStartMs + DAY_MS;
  const evtStartMs = new Date(startISO).getTime();
  const evtEndMs = new Date(endISO).getTime();
  if (evtEndMs <= dayStartMs || evtStartMs >= dayEndMs) return null;
  const clampedStart = Math.max(evtStartMs, dayStartMs);
  const clampedEnd = Math.min(evtEndMs, dayEndMs);
  return {
    startHour: (clampedStart - dayStartMs) / HOUR_MS,
    endHour: (clampedEnd - dayStartMs) / HOUR_MS,
  };
}

/**
 * Builds an ISO UTC string for "10:00 AM PT on the given Pacific day key".
 * Used when the user clicks an empty month cell — pre-fill the new-event form
 * with a sensible default time.
 */
export function isoAtPtHourOnDay(dayKey: string, hour: number): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  let guess = new Date(Date.UTC(y, m - 1, d, hour + 7, 0, 0));
  for (let i = 0; i < 3; i++) {
    const ptHr = ptHoursSinceMidnight(guess.toISOString());
    const delta = ptHr - hour;
    if (Math.abs(delta) < 0.01) break;
    guess = new Date(guess.getTime() - delta * HOUR_MS);
  }
  return guess.toISOString();
}
